import React, { Component } from "react";
import { Card, CardBody, Col, Row } from "reactstrap";
import swal from "sweetalert2";
import { saveAs } from "file-saver";
import ProgressBar from "./ProgressBar";
import SecretKeyForm from "../../Components/KeyholderSecretKeyForm";
import KeyholdersCard from "./KeyholdersCard";
import BackupOrSkip from "./BackupOrSkip";
import FinalStep from "./FinalStep";
import LoadingOverlay from "../../Components/LoadingOverlay";
import { createScryptHash, aesEncrypt } from "../crypto-utils";
import { APP_CONST, getStoredItem } from "../../../store/dataStore";
import keyholderApi from "../../../api/keyholder";
import { handleError as originalHandleError } from "@/util/errorHandler";

const handleError = error =>
  originalHandleError(error, { title: "An error occured" });

class RegistrationStep extends Component {
  state = {
    isLoading: true,
    step: 0,
    isBackupSaved: false,
    secretKey: null,
    aeskey: null,
    keyholdersList: [],
    isBankAccountCreated: false,
  };

  componentDidMount() {
    this.initialLoad();
  }

  initialLoad = async () => {
    try {
      const { status } = await keyholderApi.getStatusOfCurrentCase();

      if (status === "INSTALLED") {
        this.setState({ isBankAccountCreated: true, isLoading: false });
        this.onGoToFinalStep();
      } else {
        await this.fetchKeyholdersList();
        this.checkCurrentKeyholderStatus();
      }
    } catch (err) {
      handleError(err);
    }
  };

  /*
   * checks current keyholder status from keyholderList
   * and shows final step if keyholder has sended his secretKey
   */
  checkCurrentKeyholderStatus = () => {
    let {
      userAccountDTO: { login },
    } = JSON.parse(getStoredItem(APP_CONST.USER_INFO));

    const statusOfCurrentKeyholder = this.state.keyholdersList.find(
      k => k.username === login,
    );

    if (this.state.keyholdersList.every(k => k.status)) {
      this.fetchStatusOfBankAccountCreation();
    }

    if (!statusOfCurrentKeyholder) {
      throw new Error("Current keyholder not found in keyholder list");
    }

    if (statusOfCurrentKeyholder.status) {
      this.onGoToFinalStep();
    }
  };

  fetchKeyholdersList = async () => {
    try {
      return await keyholderApi.getKeyholdersList().then(keyholdersList => {
        this.setState({ keyholdersList });
      });
    } catch (err) {
      handleError(err);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  fetchStatusOfBankAccountCreation = () => {
    return keyholderApi
      .getStatusOfBankAccountCreation()
      .then(({ accountStatus }) => {
        this.setState({ isBankAccountCreated: accountStatus === "ACTIVATED" });
      })
      .catch(handleError);
  };

  onInitialSubmit = async ({ secretKey }) => {
    try {
      this.setState({ isLoading: true, secretKey });

      const { aeskey, salt } = await keyholderApi.getSaltAndAeskey();

      this.setState({ aeskey });

      const hash = await createScryptHash(secretKey, salt);
      const keyholdersList = await keyholderApi.submitSecretKeyHash(hash);

      this.setState({ step: 1, keyholdersList });

      if (keyholdersList.every(k => k.status)) {
        await keyholderApi.createBankAccountRequest();
        swal(
          "All secret keys have been submitted",
          "Bank account is in the process of creation!",
          "success",
        );
      }
    } catch (err) {
      handleError(err);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  onBackup = () => {
    try {
      const { secretKey, aeskey } = this.state;

      this.onSaveBackupFile(secretKey, aeskey);
      this.onGoToFinalStep();
    } catch (err) {
      handleError(err);
    }
  };

  onSaveBackupFile = (secretKey, aeskey) => {
    if (!secretKey) throw new Error("No secret key provided");
    if (!aeskey) throw new Error("No aeskey provided");

    const encryptedSecretKey = aesEncrypt(secretKey, aeskey);
    const blob = new Blob([encryptedSecretKey], {
      type: "text/plain;charset=utf-8",
    });

    const username = getStoredItem(APP_CONST.USERNAME);

    // open an alert instead in e2e test environment
    if (window.Cypress) {
      alert("downloading a file");
    } else {
      saveAs(blob, `${username}.key.priv`);
    }

    this.setState({ isBackupSaved: true });
  };

  onGoToFinalStep = () => {
    this.setState({ step: 2 });
  };

  render() {
    const {
      isLoading,
      step,
      keyholdersList,
      isBackupSaved,
      isBankAccountCreated,
    } = this.state;

    return (
      <Card>
        <CardBody>
          <LoadingOverlay loading={isLoading} />
          <Card>
            <CardBody>
              <ProgressBar step={step} />
            </CardBody>
          </Card>

          <Row>
            <Col
              lg={isBankAccountCreated ? "12" : "8"}
              sm={isBankAccountCreated ? "12" : "7"}>
              {step === 0 && <SecretKeyForm onSubmit={this.onInitialSubmit} />}
              {step === 1 && (
                <BackupOrSkip
                  onBackup={this.onBackup}
                  onSkip={this.onGoToFinalStep}
                />
              )}
              {step === 2 && (
                <FinalStep
                  onSaveBackupFile={this.onSaveBackupFile}
                  isBackupSaved={isBackupSaved}
                  isEachSecretKeySubmitted={keyholdersList.every(k => k.status)}
                  isBankAccountCreated={isBankAccountCreated}
                />
              )}
            </Col>
            {!isBankAccountCreated && (
              <Col lg="4" sm="5">
                <KeyholdersCard
                  keyholdersList={keyholdersList}
                  isBankAccountCreated={isBankAccountCreated}
                />
              </Col>
            )}
          </Row>
        </CardBody>
      </Card>
    );
  }
}

export default RegistrationStep;
